// Small pieces the phone's screens share (Stage 4.8f.4a): an initial in a circle, a presence dot, a chip, a round key, a segmented
// control, a live timer. Tokens only (design-system/tokens.css); logical properties for right-to-left.
import { Box, ButtonBase, Typography } from "@mui/material";
import { PRESENCE_COLOR } from "./phoneFormat";

export function Initial({ name, size = 36, tone = "violet" }) {
  const ch = String(name ?? "").trim()[0] ?? "#";
  const bg = tone === "cyan" ? "var(--c-cyan-bg)" : "var(--c-violet-bg)";
  const fg = tone === "cyan" ? "var(--c-cyan)" : "var(--c-violet)";
  return (
    <Box aria-hidden="true" sx={{ width: size, height: size, flexShrink: 0, borderRadius: "50%", display: "grid", placeItems: "center", background: bg, color: fg,
      font: `700 ${Math.round(size * 0.4)}px/1 var(--font-ui)`, fontFamily: "var(--font-ui), var(--font-ar)", textTransform: "uppercase" }}>
      {ch}
    </Box>
  );
}

export function WithDot({ children, status }) {
  return (
    <Box sx={{ position: "relative", flexShrink: 0, display: "inline-flex" }}>
      {children}
      {status && (
        <Box aria-hidden="true" sx={{ position: "absolute", insetInlineEnd: -1, bottom: -1, width: 12, height: 12, borderRadius: "50%",
          border: "2px solid var(--surface-card)", background: PRESENCE_COLOR[status] ?? "var(--ink-400)" }} />
      )}
    </Box>
  );
}

export function Chip({ children, tone = "plain", onClick, title, sx }) {
  const tones = {
    plain: { bg: "var(--surface-2)", fg: "var(--ink-600)" },
    ok: { bg: "var(--success-bg)", fg: "var(--ink-800)" },
    warn: { bg: "var(--warning-bg)", fg: "var(--ink-800)" },
    bad: { bg: "var(--danger-bg)", fg: "var(--ink-800)" },
    violet: { bg: "var(--c-violet-bg)", fg: "var(--c-violet)" },
  };
  const c = tones[tone] ?? tones.plain;
  const Comp = onClick ? ButtonBase : Box;
  return (
    <Comp onClick={onClick} title={title} sx={{ display: "inline-flex", alignItems: "center", gap: "6px", minHeight: 26, px: "10px", borderRadius: "var(--r-pill)",
      background: c.bg, color: c.fg, font: "600 12px/1.2 var(--font-ui)", ...(onClick ? { border: "1px dashed var(--line-200)", cursor: "pointer" } : {}), ...sx }}>
      {children}
    </Comp>
  );
}

export function Dot({ color }) {
  return <Box component="span" aria-hidden="true" sx={{ width: 7, height: 7, borderRadius: "50%", background: color, flexShrink: 0 }} />;
}

// The phone's sections / a filter row: one pressed.
export function Segments({ items, value, onChange, label, dark = false }) {
  return (
    <Box role="tablist" aria-label={label} sx={dark ? { display: "flex", gap: "6px", flexWrap: "wrap" } : { display: "flex", gap: "4px", p: "4px", background: "var(--surface-2)", borderRadius: "12px" }}>
      {items.map((it) => {
        const on = it.value === value;
        return (
          <ButtonBase key={it.value} role="tab" aria-selected={on} onClick={() => onChange(it.value)}
            sx={dark
              ? { height: 30, px: "12px", borderRadius: "var(--r-pill)", border: on ? 0 : "1px solid var(--line-200)", background: on ? "var(--ink-900)" : "var(--surface-card)",
                  color: on ? "#fff" : "var(--ink-800)", font: "600 12px/1 var(--font-ui)" }
              : { flex: 1, gap: "6px", height: 34, borderRadius: "9px", background: on ? "var(--surface-card)" : "transparent",
                  boxShadow: on ? "var(--shadow-xs)" : "none", color: on ? "var(--ink-900)" : "var(--ink-600)", font: `${on ? 700 : 600} 13px/1 var(--font-ui)` }}>
            {it.label}
            {it.badge ? (
              <Box component="span" sx={{ minWidth: 18, height: 18, px: "5px", boxSizing: "border-box", borderRadius: "var(--r-pill)", background: "var(--danger)", color: "#fff",
                font: "700 11px/18px var(--font-ui)", textAlign: "center" }}>{it.badge}</Box>
            ) : null}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

// A round control of the call screen: an icon over its word.
export function CallKey({ icon, label, onClick, active = false, disabled = false, tone = "plain" }) {
  const Icon = icon;
  const bg = active ? "var(--ink-900)" : tone === "danger" ? "var(--danger)" : "var(--surface-2)";
  const fg = active || tone === "danger" ? "#fff" : "var(--ink-800)";
  return (
    <ButtonBase onClick={onClick} disabled={disabled} aria-pressed={active} aria-label={label}
      sx={{ minHeight: 56, borderRadius: "12px", border: "1px solid var(--line-100)", background: bg, color: fg, display: "flex", flexDirection: "column", gap: "4px",
        opacity: disabled ? 0.45 : 1, "&:focus-visible": { outline: "2px solid var(--brand-violet)", outlineOffset: 2 } }}>
      <Icon size={18} strokeWidth={1.8} aria-hidden="true" />
      <Typography component="span" sx={{ font: "600 12px/1 var(--font-ui)", color: "inherit" }}>{label}</Typography>
    </ButtonBase>
  );
}

// A small square call button on a list row.
export function RowCall({ onClick, label, disabled = false, icon }) {
  const Icon = icon;
  return (
    <ButtonBase onClick={onClick} disabled={disabled} aria-label={label} title={label}
      sx={{ width: 40, height: 40, flexShrink: 0, borderRadius: "10px", background: "var(--c-violet-bg)", color: "var(--c-violet)", opacity: disabled ? 0.4 : 1,
        "&:focus-visible": { outline: "2px solid var(--brand-violet)", outlineOffset: 2 } }}>
      <Icon size={18} strokeWidth={1.8} aria-hidden="true" />
    </ButtonBase>
  );
}

export function SectionTitle({ children }) {
  return <Typography component="h3" sx={{ m: "10px 16px 4px", font: "700 11px/1 var(--font-ui)", letterSpacing: ".8px", textTransform: "uppercase", color: "var(--ink-500)" }}>{children}</Typography>;
}
